/**
 * components/landing/home/architecture/InfoPanel.jsx — the left rail of
 * the Phase 2.2 Interactive Platform Architecture.
 *
 * Renders detailed information about the currently-selected module.
 * When no module is active we show the currently-active *view mode*'s
 * summary instead — this keeps the panel from ever looking empty.
 */

import React from 'react'
import clsx from 'clsx'
import { ArrowRight, Sparkles, Zap, Link2 } from 'lucide-react'
import Link from '@/shims/NextLink'
import { getModule, TONE_COLORS, VIEW_MODES } from './data'

export default function InfoPanel({ activeId, viewMode, onSelectId }) {
  const active = activeId ? getModule(activeId) : null

  if (!active) {
    return (
      <div className="flex flex-col gap-6 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 backdrop-blur-xl h-full">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-gold">
          <Sparkles className="h-3.5 w-3.5" /> Platform View
        </div>
        <div className="flex flex-col gap-3">
          <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight">
            {viewMode.label}
          </h3>
          <p className="text-sm md:text-[15px] text-zinc-400 leading-relaxed">
            {viewMode.description}
          </p>
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="flex flex-col gap-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">How to explore</div>
          <ul className="text-sm text-zinc-300 flex flex-col gap-2.5">
            <li className="flex items-start gap-2.5"><span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-gold" /> Hover any node to see its connections.</li>
            <li className="flex items-start gap-2.5"><span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-gold" /> Click a node for details, benefits and a live preview.</li>
            <li className="flex items-start gap-2.5"><span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-brand-gold" /> Switch view modes above to see how AI, trading, ops and white-label layers compose.</li>
          </ul>
        </div>
        <div className="mt-auto flex flex-col gap-2">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Suggested starting points</div>
          <div className="flex flex-wrap gap-2">
            {['ai-mentor', 'trading-terminal', 'white-label', 'analytics'].map((id) => {
              const m = getModule(id)
              if (!m) return null
              const tone = TONE_COLORS[m.tone]
              return (
                <button
                  key={id}
                  onClick={() => onSelectId(id)}
                  className="text-xs font-medium rounded-full border px-3 py-1.5 transition-all hover:-translate-y-[1px]"
                  style={{
                    borderColor: `rgba(${tone.rgb},0.35)`,
                    color: tone.hex,
                    background: `rgba(${tone.rgb},0.08)`,
                  }}
                >
                  {m.name}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const tone = TONE_COLORS[active.tone] || TONE_COLORS.orange
  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 backdrop-blur-xl h-full">
      {/* Category chip */}
      <div className="flex items-center justify-between">
        <span
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]"
          style={{ borderColor: `rgba(${tone.rgb},0.35)`, color: tone.hex, background: `rgba(${tone.rgb},0.08)` }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.hex }} />
          {active.category === 'ai'      ? 'AI'
          : active.category === 'trading' ? 'Trading'
          : active.category === 'ops'     ? 'Operations'
          : active.category === 'wl'      ? 'White-Label'
          : 'Infrastructure'}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">
          Module · {active.ring === 'inner' ? 'Core' : 'Platform'}
        </span>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight">
          {active.name}
        </h3>
        <p className="text-sm md:text-[15px] text-zinc-400 leading-relaxed">
          {active.overview}
        </p>
      </div>

      {/* Business value */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Why it matters</div>
        <div className="text-sm text-zinc-200 leading-relaxed">{active.businessValue}</div>
      </div>

      {/* Features + Benefits */}
      <div className="grid grid-cols-1 gap-4">
        <FeatureList title="Core capabilities" items={active.features} tone={tone} />
        <BenefitList title="Business benefits" items={active.benefits} />
      </div>

      {/* Related modules */}
      {active.connections?.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
            <Link2 className="h-3 w-3" /> Connected to
          </div>
          <div className="flex flex-wrap gap-1.5">
            {active.connections.map((id) => {
              const m = getModule(id)
              if (!m) return null
              const t = TONE_COLORS[m.tone]
              return (
                <button
                  key={id}
                  onClick={() => onSelectId(id)}
                  className="text-[11px] font-medium rounded-full border px-2.5 py-1 transition-all hover:-translate-y-[1px] hover:brightness-125"
                  style={{ borderColor: `rgba(${t.rgb},0.30)`, color: t.hex, background: `rgba(${t.rgb},0.05)` }}
                >
                  {m.name}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="mt-auto pt-2 flex flex-col sm:flex-row gap-2">
        <Link
          href={active.cta?.href || '/platform'}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-black transition-all hover:-translate-y-[1px]"
          style={{
            background: `linear-gradient(90deg, ${tone.hex}, #FFE17A)`,
            boxShadow: `0 10px 40px -10px rgba(${tone.rgb}, 0.55)`,
          }}
        >
          {active.cta?.label || 'Learn more'} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          href="/contact#demo"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-[1px] hover:bg-white/[0.06]"
        >
          Schedule demo
        </Link>
      </div>
    </div>
  )
}

function FeatureList({ title, items, tone }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title}</div>
      <ul className="grid grid-cols-1 gap-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
            <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: tone.hex }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function BenefitList({ title, items }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title}</div>
      <ul className="grid grid-cols-1 gap-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-emerald" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
